import { useLocation } from 'wouter';
import { PageLoader } from '@/components/ui/spinner';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Bell,
  Calendar,
  CircleCheck,
  CircleX,
  FileSpreadsheet,
  Receipt,
  TriangleAlert,
} from 'lucide-react';
import { api } from '@/lib/api';
import { AppShell } from '@/components/app-shell';
import { useTenantMe } from '@/hooks/use-tenant-me';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface NotificationRow {
  id: string;
  kind:
    | 'due_date_3d'
    | 'due_date_today'
    | 'declaration_ready'
    | 'invoice_processed'
    | 'payment_failed'
    | 'monthly_summary';
  channel: 'whatsapp' | 'email';
  payload: Record<string, unknown>;
  scheduledFor: string;
  sentAt: string | null;
  error: string | null;
  createdAt: string;
}

const KIND_CONFIG: Record<
  NotificationRow['kind'],
  { icon: typeof Bell; title: string; bg: string }
> = {
  due_date_3d: {
    icon: Calendar,
    title: 'Recordatorio de presentación',
    bg: 'bg-warning/15 text-warning',
  },
  due_date_today: {
    icon: TriangleAlert,
    title: 'Hoy vence tu presentación',
    bg: 'bg-destructive/15 text-destructive',
  },
  declaration_ready: {
    icon: FileSpreadsheet,
    title: 'Presentación generada',
    bg: 'bg-primary/15 text-primary',
  },
  invoice_processed: {
    icon: Receipt,
    title: 'Factura validada',
    bg: 'bg-success/15 text-success',
  },
  payment_failed: {
    icon: CircleX,
    title: 'Pago rechazado',
    bg: 'bg-destructive/15 text-destructive',
  },
  monthly_summary: {
    icon: Bell,
    title: 'Resumen mensual',
    bg: 'bg-muted text-muted-foreground',
  },
};

export function AppNotificationsPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useTenantMe();
  const { data, isLoading } = useQuery({
    queryKey: ['app-notifications'],
    queryFn: () => api<{ items: NotificationRow[] }>('/api/app/notifications'),
    enabled: !!me,
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/app/login');
    return null;
  }

  return (
    <AppShell>
      <div>
        <h1 className="text-2xl font-bold">Historial de notificaciones</h1>
        <p className="text-sm text-muted-foreground">
          Avisos que te mandamos por WhatsApp o email.
        </p>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      )}

      {!isLoading && data?.items.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
            <Bell className="size-10" />
            <div className="text-center">
              <p className="font-medium text-foreground">Sin notificaciones todavía</p>
              <p className="text-sm">
                Cuando tengas vencimientos próximos o se valide una factura te avisamos por acá.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {data?.items.map((n) => {
          const cfg = KIND_CONFIG[n.kind] ?? {
            icon: Bell,
            title: n.kind,
            bg: 'bg-muted text-muted-foreground',
          };
          const Icon = cfg.icon;
          const summary = renderSummary(n);
          return (
            <Card key={n.id}>
              <CardContent className="flex items-start gap-4 p-4">
                <div className={'flex aspect-square size-10 shrink-0 items-center justify-center rounded-md ' + cfg.bg}>
                  <Icon className="size-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground">
                    {format(new Date(n.createdAt), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}
                  </div>
                  <div className="font-semibold mt-0.5">{cfg.title}</div>
                  {summary && <p className="text-sm text-muted-foreground mt-1">{summary}</p>}
                  {n.error && (
                    <p className="text-xs text-destructive mt-1">Error al enviar: {n.error}</p>
                  )}
                </div>
                {n.sentAt && (
                  <CircleCheck className="size-4 text-success shrink-0 mt-1" />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </AppShell>
  );
}

function renderSummary(n: NotificationRow): string | null {
  const p = n.payload as Record<string, unknown>;
  if (n.kind === 'due_date_3d' || n.kind === 'due_date_today') {
    const net = Number(p.netToPay ?? 0);
    return net > 0
      ? `Te toca pagar Gs ${Math.round(net).toLocaleString('es-PY')}.`
      : 'No tenés monto a pagar este período.';
  }
  if (n.kind === 'invoice_processed') {
    return 'Tu factura quedó validada y cargada a tu cuenta.';
  }
  if (n.kind === 'declaration_ready') {
    return 'Tu declaración está lista para revisión.';
  }
  return null;
}
